import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useRef, useMemo } from 'react';
import { Table, Input, Button, Space, Card, Drawer } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useFetchAllClients } from '../../hooks/clientHooks';
import ClientForm from './ClientForm';

const ClientList = ({ onSelectClient, showCreateButton = true }) => {
  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [compteSearch, setCompteSearch] = useState('');
  const [villeSearch, setVilleSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sorter, setSorter] = useState({
    field: 'CLIENT',
    order: 'ascend'
  });
  
  const searchInput = useRef(null);

  // Combine all params for the API call
  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    clientSearch,
    compteSearch,
    villeSearch,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
  }), [pagination.current, pagination.pageSize, clientSearch, compteSearch, villeSearch, sorter.field, sorter.order]);

  const { clients, total, loading, error, refetch } = useFetchAllClients(params);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  // Search and filtering function
  const getColumnSearchProps = (dataIndex, placeholder, setState) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder || `Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex, setState)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex, setState)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button 
            onClick={() => handleReset(clearFilters, dataIndex, setState)} 
            size="small" 
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current && searchInput.current.select());
      }
    },
    render: text => text,
  });

  // Handling search and reset
  const handleSearch = (selectedKeys, confirm, dataIndex, setState) => {
    confirm();
    setState(selectedKeys[0] || '');
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page on new search
  };

  const handleReset = (clearFilters, dataIndex, setState) => {
    clearFilters();
    setState('');
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page on filter clear
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    if (sorter.field && sorter.order) {
      setSorter({
        field: sorter.field,
        order: sorter.order
      });
    }
  };

  if (error) return <p>Error: {error}</p>;

  const columns = [
    {
      title: 'COMPTE',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
      sorter: true,
      ...getColumnSearchProps('COMPTE', 'Search account', setCompteSearch),
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      sorter: true,
      ...getColumnSearchProps('CLIENT', 'Search client name', setClientSearch),
    },
    {
      title: 'Ville',
      dataIndex: 'VILLE',
      key: 'VILLE',
      sorter: true,
      ...getColumnSearchProps('ville', 'Search city', setVilleSearch),
    },
  ];

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };
  
  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const handleClientCreated = () => {
    setCreateDrawerOpen(false);
    refetch && refetch();
  };

  return (
    <div>
      <Card style={tableCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={titleStyle}>Liste des clients</h2>
          {showCreateButton && (
            <PermissionButton permissionModule="clients" permissionAction="create" type="primary" icon={<PlusOutlined />} onClick={() => setCreateDrawerOpen(true)}>
              Nouveau client
            </PermissionButton>
          )}
        </div>
        <Table
          columns={columns}
          dataSource={clients}
          rowKey="code_clt"
          loading={loading}
          pagination={{ 
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          size="small"
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => onSelectClient(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
      <Drawer
        title="Ajouter un client"
        placement="right"
        width={1000}
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        destroyOnClose
        bodyStyle={{ padding: 0, background: '#fafafa' }}
      >
        <div style={{ padding: 24, minHeight: '100%' }}>
          <ClientForm onSuccess={handleClientCreated} />
        </div>
      </Drawer>
    </div>
  );
};

export default ClientList;
